/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 * 
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 * 
 * Software credits: 
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 * 
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useParams, useLocation } from 'react-router-dom';
import { Form, Input, Button, Col, Space, Affix } from 'antd';

import { history } from '@utils/history';
import { resetFlow } from '@store/reducers/flows';
import { MESSAGES } from '@translations/labels';
import { useAgentId } from '@hooks/useAgentId';
import { COMMON_NAME_VALIDATION_SETTINGS } from '@common-settings';

import flowActions from '@store/actions/flows';
import { isEmpty } from '@utils/validation/isEmpty';
import { ROUTES, idField } from '@constants';
import { useAddFlow } from './useAddFlow';
import { useUpdateFlow } from './useUpdateFlow';
import flowFormStyle from './flowFormStyle';
import findInPage from './flowForm/findInPage';
import highlightCode from './flowForm/highlightCode';
import backdropFlowSizeConfig from './flowForm/backdropFlowSizeConfig';
import BackDropFlowContainer from './flowForm/BackDropFlowContainer';

const FlowForm = ({ editMode, readOnly }) => {
  const textAreaRef = React.createRef();
  const highlightsRef = React.createRef();
  const backdropRef = React.createRef();

  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { agentId } = useAgentId();
  const params = useParams();
  const { flowId } = params;
  const labels = { ...MESSAGES.manageBot, ...MESSAGES.commons };

  const { TextArea } = Input;

  const { updateFlow } = useUpdateFlow();
  const { addFlow } = useAddFlow();

  const { search } = useLocation();
  const findString = new URLSearchParams(search).get('find');

  useEffect(() => {
    if (!isEmpty(agentId) && !isEmpty(flowId)) {
      dispatch(flowActions.getFlow(agentId, flowId));
    }
  }, [agentId]);

  useEffect(() => {
    history.listen(() => {
      dispatch(resetFlow());
    });
    if (findString) findInPage(findString);
  }, []);

  useEffect(() => {
    highlightCode(findString, { highlightsRef, textAreaRef });
  }, [highlightsRef, textAreaRef]);

  backdropFlowSizeConfig();

  const flow = useSelector(state => state.flows.current);
  const flows = useSelector(state => state.flows.all);
  const isMainCase = (flow, flows) => isEmpty(flow) && flows && !flows.length;

  useEffect(() => {
    if (isEmpty(flow)) {
      dispatch(flowActions.getFlows(agentId));
    }
  }, [!!flow]);

  const onReset = () => {
    form.resetFields();
  };

  const setValue = (field, text) => form.setFieldsValue({ [field]: text });

  if (!isEmpty(flow)) {
    setValue('name', flow.name);
    setValue('flow', flow.flow);
  } else if (isMainCase(flow, flows)) {
    setValue('name', 'main');
  }

  const handleFinish = values => {
    const flowFields = {
      name: values.name,
      flow: values.flow,
    };

    return editMode ? updateFlow(flow[idField], flowFields) : addFlow(flowFields);
  };

  return (
    <>
      <Col span={24}>
        <Form
          name={editMode ? 'edit-domain' : 'new-domain'}
          layout="vertical"
          form={form}
          onFinish={handleFinish}
          hideRequiredMark
          scrollToFirstError
        >
          <Form.Item
            name="name"
            label={labels.formFlowName}
            rules={[
              {
                required: true,

                message: `${labels.formFlowName} ${labels.fieldRequired}`,
              },
              ...COMMON_NAME_VALIDATION_SETTINGS,
            ]}
          >
            <Input
              autoFocus
              style={{ width: 400 }}
              maxLength={46}
              placeholder={labels.formInputFlowPlaceholder}
              disabled={editMode || isMainCase(flow, flows)}
              style={{ width: 420 }}
              autoComplete="off"
              maxLength="46"
              allowClear
            />
          </Form.Item>
          <BackDropFlowContainer backdropRef={backdropRef} highlightsRef={highlightsRef} />

          <div id="buttonDivFooter">
            <Affix
              offsetTop={120}
              className="text-right"
              style={{
                marginRight: '1em',
                zIndex: 9,
                position: 'relative',
                width: '215px',
                float: 'right',
              }}
            >
              <Space id="buttonFooterSubmit" size="middle" style={{ zIndex: 99 }}>
                <Button type="link" htmlType="button" onClick={onReset} className="black uppercase">
                  <Link to={ROUTES.manageBot.manageFlow.path} className="link-secondary block">
                    {labels.btnCancel}
                  </Link>
                </Button>
                {!readOnly && (
                  <Button type="primary" htmlType="submit" className="uppercase">
                    {editMode ? labels.btnEditFlow : labels.btnCreateFlow}
                  </Button>
                )}
              </Space>
            </Affix>
          </div>

          <Form.Item
            className="flow_description"
            name="flow"
            label={labels.formFlowContent}
            rules={[
              {
                required: true,
                message: `${labels.formFlowContent} ${labels.fieldRequired}`,
              },
            ]}
          >
            <TextArea
              id="flowtextAreaForm"
              ref={textAreaRef}
              placeholder={labels.formInputFlowContentPlaceholder}
              maxLength={100000}
              showCount
              autoSize
              allowClear
              readOnly={readOnly}
              disabled={readOnly}
              onChange={() => {
                highlightCode(findString, { highlightsRef, textAreaRef });
              }}
            />
          </Form.Item>
          {/* TODO: 
            Add list of flows called from here 
            Related Information about this flow in this col, current dialog, related dialogs.
          }
          <section>
            <div>
              <h5>Related flows (TODO)</h5>
            </div>

            <ul>
              <li>Flow 2</li>
              <li>Flow 3</li>
            </ul>
          </section>
          { */}

          <Form.Item className="text-right" />
        </Form>
      </Col>
      <style jsx>{flowFormStyle}</style>
    </>
  );
};

export { FlowForm };
